import { requestStartPlayback, releasePlayback } from "./audioPlaybackGuard";
import { sanitizeTextForSpeech, toSpeechLocale } from "./speechText";
import { resolveBestSpeechVoice } from "./tts/voiceResolver";
import {
  getPremiumSpeechSettings,
  shouldRepeatForLearning,
  getPauseBetweenRepeatsMs,
} from "./tts/pronunciationProfile";

/** Cache for voices loaded via onvoiceschanged (Chrome returns [] until then). */
let cachedVoices: SpeechSynthesisVoice[] = [];

/** Used to cancel scheduled repeat when a new playback starts. */
let repeatTimeoutId: ReturnType<typeof setTimeout> | null = null;
let playbackGeneration = 0;

function cacheVoices(): void {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  const list = window.speechSynthesis.getVoices();
  if (list.length > 0) cachedVoices = list;
}

if (typeof window !== "undefined" && window.speechSynthesis) {
  cacheVoices();
  window.speechSynthesis.addEventListener("voiceschanged", cacheVoices);
}

/**
 * Returns voices, preferring fresh list. Uses cache if getVoices() is still empty
 * (happens in Chrome until voiceschanged fires).
 */
function getVoicesSync(): SpeechSynthesisVoice[] {
  if (typeof window === "undefined" || !window.speechSynthesis) return [];
  const fresh = window.speechSynthesis.getVoices();
  return fresh.length > 0 ? fresh : cachedVoices;
}

/**
 * Ensures voices are loaded before speaking. Chrome often returns [] from getVoices()
 * until onvoiceschanged fires. Resolves when we have voices or after timeout.
 */
function getVoicesAsync(): Promise<SpeechSynthesisVoice[]> {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    return Promise.resolve([]);
  }
  return new Promise((resolve) => {
    const tryResolve = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        cachedVoices = voices;
        resolve(voices);
        return true;
      }
      const cached = cachedVoices;
      if (cached.length > 0) {
        resolve(cached);
        return true;
      }
      return false;
    };

    if (tryResolve()) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    const onChanged = () => {
      window.speechSynthesis.removeEventListener("voiceschanged", onChanged);
      clearTimeout(timeoutId);
      cacheVoices();
      resolve(getVoicesSync());
    };
    window.speechSynthesis.addEventListener("voiceschanged", onChanged);

    timeoutId = setTimeout(() => {
      window.speechSynthesis.removeEventListener("voiceschanged", onChanged);
      cacheVoices();
      resolve(getVoicesSync());
    }, 2000);
  });
}

/**
 * Uses centralized voice resolver for native-quality locale matching.
 * Fallback: exact locale → same language different region → same base language.
 * Never falls back to unrelated language if same-language voice exists.
 */

/** Tracks the HTMLAudioElement from playAudioUrl to prevent overlapping playback. */
let currentUrlAudio: HTMLAudioElement | null = null;

/** Play pre-generated audio from URL (Neural TTS). Falls back to SpeechSynthesis on error. */
function playAudioUrl(
  url: string,
  volume: number,
  onEnd?: () => void,
  fallback?: () => void
): void {
  if (typeof window === "undefined") return;
  // Stop previous URL-based audio to prevent overlap
  if (currentUrlAudio) {
    currentUrlAudio.onended = null;
    currentUrlAudio.onerror = null;
    currentUrlAudio.pause();
    currentUrlAudio.src = "";
    currentUrlAudio = null;
  }
  try {
    const audio = new Audio(url);
    audio.volume = Math.max(0, Math.min(1, volume));
    audio.onended = () => {
      if (currentUrlAudio === audio) currentUrlAudio = null;
      onEnd?.();
    };
    audio.onerror = () => {
      if (currentUrlAudio === audio) currentUrlAudio = null;
      if (fallback) fallback();
      else onEnd?.();
    };
    currentUrlAudio = audio;
    audio.play().catch(() => fallback?.());
  } catch {
    fallback?.();
  }
}

/**
 * Speaks the given text. Prefers pre-generated Neural TTS audio (audioUrl) when provided;
 * otherwise uses Web Speech API (browser TTS) with premium pronunciation profile.
 * Order: resolve language → resolve best voice → apply premium settings → speak (with optional repeat).
 */
export function speakGameWord(
  text: string,
  languageCode?: string,
  volume?: number,
  onEnd?: () => void,
  audioUrl?: string | null
): void {
  if (typeof window === "undefined") return;

  const trimmed = sanitizeTextForSpeech(text ?? "");
  if (!trimmed) return;

  if (!requestStartPlayback()) return;

  const wrappedOnEnd = () => {
    releasePlayback();
    onEnd?.();
  };

  const vol = typeof volume === "number" ? Math.max(0, Math.min(1, volume)) : 1;

  playbackGeneration += 1;
  if (repeatTimeoutId != null) {
    clearTimeout(repeatTimeoutId);
    repeatTimeoutId = null;
  }

  if (audioUrl && audioUrl.startsWith("http")) {
    playAudioUrl(audioUrl, vol, wrappedOnEnd, () =>
      speakWithBrowserTts(trimmed, languageCode ?? "en", vol, playbackGeneration, wrappedOnEnd)
    );
    return;
  }

  speakWithBrowserTts(trimmed, languageCode ?? "en", vol, playbackGeneration, wrappedOnEnd);
}

function speakWithBrowserTts(
  text: string,
  languageCode: string,
  volume: number,
  generation: number,
  onEnd?: () => void
): void {
  if (typeof window === "undefined" || !window.speechSynthesis) return;

  const safeText = sanitizeTextForSpeech(text);
  if (!safeText) return;
  const safeLang = toSpeechLocale(languageCode, "en-US");
  const { rate, pitch } = getPremiumSpeechSettings();
  const repeat = shouldRepeatForLearning(safeText);
  const pauseBetweenMs = getPauseBetweenRepeatsMs();

  void (async () => {
    const voices = await getVoicesAsync();
    const matchedVoice = resolveBestSpeechVoice(safeLang, voices);

    const speakOne = (finalOnEnd?: () => void) => {
      const utterance = new SpeechSynthesisUtterance(safeText);
      utterance.lang = safeLang;
      utterance.volume = volume;
      utterance.rate = Math.max(0.5, Math.min(2, rate));
      utterance.pitch = Math.max(0.5, Math.min(2, pitch));
      if (matchedVoice) utterance.voice = matchedVoice;
      utterance.onerror = (e) => {
        console.error("[TTS Error]", e);
        finalOnEnd?.();
      };
      if (finalOnEnd) utterance.onend = finalOnEnd;
      window.speechSynthesis.speak(utterance);
    };

    window.speechSynthesis.cancel();
    const startDelayMs = 50;

    if (repeat) {
      setTimeout(() => {
        if (generation !== playbackGeneration) return;
        speakOne(() => {
          if (generation !== playbackGeneration) return;
          repeatTimeoutId = setTimeout(() => {
            repeatTimeoutId = null;
            if (generation !== playbackGeneration) return;
            speakOne(onEnd);
          }, pauseBetweenMs);
        });
      }, startDelayMs);
    } else {
      setTimeout(() => speakOne(onEnd), startDelayMs);
    }
  })();
}

/** Alias for speakGameWord. */
export const speakWord = speakGameWord;
